import React from 'react';
import Box from '@mui/material/Box';
import { ServiceCategory } from '@prisma/client';
import { Grid2, Stack } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import Error from '../../global/Error';
import MyDateRangePicker from '../../filtering/DateRangePicker';
import SingleSelectFilter from '../../filtering/SingleSelectFilter';
import MultiSelectFilter from '../../filtering/MultiSelectFilter';
import ColoredAlert from '../../global/ColoredAlert';
import useReports from '../../../hooks/reports/useReports';
import VirtualizedTable from '../../table/VirtualizedTable';
import {
  ADDITIONAL_SERVICES_TABLE_COLUMNS,
  APPT_TABLE_COLUMNS,
  EXPORTS_QUERY_KEY,
  ORDER_TABLE_COLUMNS,
  RETAIL_CHECKOUTS_TABLE_DESCRIPTION,
  TABLE_HEADER_CONTENT,
  WARNING_CONTENT,
} from '../../../constants/reports';
import {
  AdditionalServiceRow,
  AppointmentRow,
  DateType,
  OrderRow,
  TransactionType,
} from '../../../types/reports';
import RevenueReport from '../RevenueReport';
import ReportBox from '../ReportBox';
import CustomPieChart from '../PieChart';
import { formatDollarAmount } from '../../../utils/global/helper';
import EmptyReport from '../EmptyReport';
import AppointmentSideBarModal from '../../appointments/AppointmentSideBarModal';
import { invalidateAppointment } from '../../../services/appointments';
import PaymentsSideBarModal from '../../payments/PaymentsSideBarModal';
import { invalidatePaymentPaymentsGroup } from '../../../services/payments';
import {
  createReportExport,
  ReportExportType,
} from '../../../services/reports';
import useConfirm from '../../../hooks/useConfirm';
import useLoadingModal from '../../../hooks/global/useLoadingModal';

interface Props {
  onGoToExports: () => void;
}

export default function ReportsTab({ onGoToExports }: Props) {
  const [selectedAppointmentId, setSelectedAppointmentId] = React.useState<
    string | null
  >(null);
  const [selectedPaymentId, setSelectedPaymentId] = React.useState<
    string | null
  >(null);

  const queryClient = useQueryClient();

  const {
    totalsQuery,
    appointmentsQuery,
    additionalServicesQuery,
    ordersQuery,
    exportFilters,
    state: {
      servicesFilter,
      dateTypeFilter,
      artistFilter,
      tripFilter,
      transactionTypeFilter,
      productsFilter,
      inactiveEmployeesFilter,
      isManager,
      userId,
      ...state
    },
  } = useReports();

  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const queueExport = React.useCallback(
    async (type: ReportExportType) => {
      try {
        openLoadingModal({
          header: 'Queueing export',
          message: 'Your export is being queued. Please wait...',
        });
        await createReportExport(type, exportFilters);
        queryClient.invalidateQueries({ queryKey: EXPORTS_QUERY_KEY });
        closeLoadingModal();
        await confirm({
          title: 'Export started',
          message:
            "We're generating your CSV in the background. You'll get an email when it's ready, and you can also find it under Reports → Exports.",
          positiveText: 'Okay',
          negativeText: '',
        });
        onGoToExports();
      } catch {
        toast.error('Failed to queue export');
      } finally {
        closeLoadingModal();
      }
    },
    [
      openLoadingModal,
      exportFilters,
      queryClient,
      closeLoadingModal,
      confirm,
      onGoToExports,
    ],
  );

  // The api returns table-ready rows; appointments + unlinked deposits share
  // the appointments table shape, so we concatenate them for display.
  const appointmentsTableData = React.useMemo<AppointmentRow[]>(
    () =>
      appointmentsQuery.data?.pages.flatMap(p => [
        ...p.result.appointments,
        ...p.result.unlinkedDeposits,
      ]) ?? [],
    [appointmentsQuery.data],
  );
  const additionalServicesTableData = React.useMemo<AdditionalServiceRow[]>(
    () => additionalServicesQuery.data?.pages.flatMap(p => p.result) ?? [],
    [additionalServicesQuery.data],
  );
  const ordersTableData = React.useMemo<OrderRow[]>(
    () => ordersQuery.data?.pages.flatMap(p => p.result) ?? [],
    [ordersQuery.data],
  );

  // Zeros while loading so the summary cards render without flicker.
  const totals = totalsQuery.data ?? {
    appointmentCount: 0,
    orderCount: 0,
    totalSales: 0,
    cashSales: 0,
    digitalSales: 0,
    creditSales: 0,
    giftCardSales: 0,
    totalTips: { cash: 0, digital: 0, credit: 0, giftCard: 0, total: 0 },
    salesTax: { cash: 0, digital: 0, credit: 0, giftCard: 0, total: 0 },
  };
  const {
    cashSales,
    digitalSales,
    creditSales,
    giftCardSales,
    totalSales,
    totalTips,
    salesTax,
  } = totals;

  const isQueryError =
    totalsQuery.isError ||
    appointmentsQuery.isError ||
    additionalServicesQuery.isError ||
    ordersQuery.isError;

  const isTotalsLoading = totalsQuery.isLoading;
  const isAppointmentsLoading = appointmentsQuery.isLoading;
  const isAdditionalServicesLoading = additionalServicesQuery.isLoading;
  const isOrdersLoading = ordersQuery.isLoading;

  const isQueryFetching =
    totalsQuery.isFetching ||
    appointmentsQuery.isFetching ||
    additionalServicesQuery.isFetching ||
    ordersQuery.isFetching;

  const onAppointmentClick = React.useCallback(
    (appointment: AppointmentRow) => {
      if (appointment?.paymentId) {
        const paymentId = appointment.paymentId as string;
        invalidatePaymentPaymentsGroup(paymentId);
        setSelectedPaymentId(paymentId);
        return;
      }
      if (!appointment?.appointmentId) return;
      const appointmentId = appointment.appointmentId as string;
      invalidateAppointment(appointmentId);
      setSelectedAppointmentId(appointmentId);
    },
    [],
  );

  const onOrderClick = React.useCallback((order: OrderRow) => {
    if (!order?.paymentId) return;
    const paymentId = order.paymentId as string;
    invalidatePaymentPaymentsGroup(paymentId);
    setSelectedPaymentId(paymentId);
  }, []);

  const onAdditionalServiceClick = React.useCallback(
    (row: AdditionalServiceRow) => {
      if (!row?.appointmentId) return;
      const appointmentId = row.appointmentId as string;
      invalidateAppointment(appointmentId);
      setSelectedAppointmentId(appointmentId);
    },
    [],
  );

  if (isQueryError) {
    return (
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 200px)',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Error errorMessage="Error fetching report data" />
      </Box>
    );
  }

  return (
    <Stack gap={3}>
      <div className="calendar-header reports-header">
        <Box
          display="flex"
          gap={1}
          className="header-left"
          component="div"
          sx={{
            overflowX: 'auto',
            overflowY: 'hidden',
          }}>
          <div className="joined-pickers">
            <MyDateRangePicker
              storageKey="ReportsDateRange"
              value={state.reportRange}
              setValue={state.setReportRange}
              maxDateRangeMonths={state.maxDateRangeMonths}
            />
            <SingleSelectFilter
              name={dateTypeFilter.name}
              options={dateTypeFilter.options}
              state={dateTypeFilter.value}
              setState={newState =>
                dateTypeFilter.setValue(newState as DateType)
              }
              styleOverride={{ minWidth: '250px' }}
            />
          </div>
          {state.showArtistFilter && (
            <MultiSelectFilter
              emptyLabel="All artists"
              allLabel="All artists"
              name={artistFilter.name}
              options={artistFilter.options}
              state={artistFilter.value}
              // sorting prevents refetching when the order of selected employees changes
              setState={newSelected => {
                const arrValue = Array.isArray(newSelected)
                  ? newSelected
                  : newSelected(artistFilter.value);
                artistFilter.setValue(arrValue.sort());
              }}
            />
          )}
          <SingleSelectFilter
            name={servicesFilter.name}
            options={servicesFilter.options}
            state={servicesFilter.value ?? 'ALL'}
            styleOverride={{ width: 'auto' }}
            setState={newState => {
              servicesFilter.setValue(newState as ServiceCategory);
            }}
          />

          {state.showTripsFilter && (
            <MultiSelectFilter
              emptyLabel="All trips"
              allLabel="All trips"
              name={tripFilter.name}
              options={tripFilter.options}
              state={tripFilter.value}
              setState={newSelected => {
                const arrValue = Array.isArray(newSelected)
                  ? newSelected
                  : newSelected(tripFilter.value);
                tripFilter.setValue(arrValue.sort());
              }}
            />
          )}
          {state.showOrdersOptions && (
            <SingleSelectFilter
              name={transactionTypeFilter.name}
              options={transactionTypeFilter.options}
              state={transactionTypeFilter.value ?? 'ALL'}
              styleOverride={{ width: 'auto' }}
              setState={newState =>
                transactionTypeFilter.setValue(newState as TransactionType)
              }
            />
          )}
          {state.showProductsOptions && (
            <MultiSelectFilter
              emptyLabel="Select products"
              allLabel="All products"
              name={productsFilter.name}
              options={productsFilter.options}
              state={productsFilter.value}
              setState={newSelected => {
                const arrValue = Array.isArray(newSelected)
                  ? newSelected
                  : newSelected(productsFilter.value);
                productsFilter.setValue(arrValue.sort());
              }}
            />
          )}
          {state.showInactiveEmployeesOption && (
            <SingleSelectFilter
              name={inactiveEmployeesFilter.name}
              options={inactiveEmployeesFilter.options}
              state={inactiveEmployeesFilter.value.toString()}
              setState={newState =>
                inactiveEmployeesFilter.setValue(newState === 'true')
              }
              styleOverride={{ minWidth: '255px' }}
            />
          )}
        </Box>
      </div>
      <Box width="100%" display="flex" flexDirection="column" gap={3}>
        <ColoredAlert
          type="warning"
          title={WARNING_CONTENT[dateTypeFilter.value].title}
          subtitle={WARNING_CONTENT[dateTypeFilter.value].subtitle}
          animate={false}
        />

        <RevenueReport
          totalSales={totalSales}
          cashSales={cashSales}
          digitalSales={digitalSales}
          creditSales={creditSales}
          giftCardSales={giftCardSales}
          isRefetching={isTotalsLoading || totalsQuery.isRefetching}
          headerContent={{
            title: 'Revenue Report',
            subtitle:
              'Based on the filters selected above, this report will show your total revenue broken out by credit, cash, and gift cards. Revenue report DOES NOT include tips and tax.',
          }}
        />

        <Grid2 container spacing={2}>
          <Grid2 size={{ sm: 12, md: 6 }}>
            <ReportBox
              isLoading={isTotalsLoading}
              headerContent={{
                title: 'Tips Report',
                subtitle:
                  'Based on the filters selected above, this report will show your staff’s tips broken out by credit, cash, and gift cards.',
              }}>
              {isTotalsLoading ? (
                <CustomPieChart data={null} dataKey="value" xAxisKey="name" />
              ) : totalTips.total ? (
                <CustomPieChart
                  data={[
                    { name: 'Cash', value: totalTips.cash },
                    { name: 'Digital', value: totalTips.digital },
                    { name: 'Credit', value: totalTips.credit },
                    { name: 'Gift Card', value: totalTips.giftCard },
                  ]}
                  dataKey="value"
                  xAxisKey="name"
                  type="dollar"
                  withTooltip
                  chartTotalValue={formatDollarAmount(totalTips.total)}
                />
              ) : (
                <EmptyReport />
              )}
            </ReportBox>
          </Grid2>
          <Grid2 size={{ sm: 12, md: 6 }}>
            <ReportBox
              isLoading={isTotalsLoading}
              headerContent={{
                title: 'Sales Tax Report',
                subtitle:
                  'Based on the filters selected above, this report will show sales tax collected broken out by credit, cash, and gift cards.',
              }}>
              {isTotalsLoading ? (
                <CustomPieChart data={null} dataKey="value" xAxisKey="name" />
              ) : salesTax.total ? (
                <CustomPieChart
                  data={[
                    { name: 'Cash', value: salesTax.cash },
                    { name: 'Digital', value: salesTax.digital },
                    { name: 'Credit', value: salesTax.credit },
                    { name: 'Gift Card', value: salesTax.giftCard },
                  ]}
                  dataKey="value"
                  xAxisKey="name"
                  type="dollar"
                  withTooltip
                  chartTotalValue={formatDollarAmount(salesTax.total)}
                />
              ) : (
                <EmptyReport />
              )}
            </ReportBox>
          </Grid2>
        </Grid2>

        <VirtualizedTable
          type="compact"
          headerContent={{
            title: 'Appointment Transactions Report',
            subtitle: TABLE_HEADER_CONTENT[dateTypeFilter.value],
            action: {
              label: 'Download',
              onClick: () => queueExport('APPOINTMENTS'),
              icon: <FileDownloadOutlinedIcon />,
              disabled: isQueryFetching,
            },
          }}
          columns={APPT_TABLE_COLUMNS}
          data={appointmentsTableData}
          height={480}
          isLoading={isAppointmentsLoading || appointmentsQuery.isRefetching}
          emptyText="No appointments found for selected date range."
          onRowClick={onAppointmentClick}
          resetScrollKey={state.resetScrollKeys.appointments}
          onEndReached={() => {
            if (
              appointmentsQuery.hasNextPage &&
              !appointmentsQuery.isFetchingNextPage
            ) {
              appointmentsQuery.fetchNextPage();
            }
          }}
        />

        {!isManager &&
          additionalServicesTableData.length > 0 &&
          transactionTypeFilter.value !== 'ORDERS' && (
            <VirtualizedTable
              type="compact"
              headerContent={{
                title: 'Services Transactions Report',
                subtitle:
                  "Services you provided that were added to another provider's appointment during checkout.",
                action: {
                  label: 'Download',
                  onClick: () => queueExport('ADDITIONAL_SERVICES'),
                  icon: <FileDownloadOutlinedIcon />,
                  disabled: isQueryFetching,
                },
              }}
              columns={ADDITIONAL_SERVICES_TABLE_COLUMNS}
              data={additionalServicesTableData}
              height={480}
              isLoading={
                isAdditionalServicesLoading ||
                additionalServicesQuery.isRefetching
              }
              emptyText="No services found for selected date range."
              onRowClick={onAdditionalServiceClick}
              resetScrollKey={state.resetScrollKeys.additionalServices}
              onEndReached={() => {
                if (
                  additionalServicesQuery.hasNextPage &&
                  !additionalServicesQuery.isFetchingNextPage
                ) {
                  additionalServicesQuery.fetchNextPage();
                }
              }}
            />
          )}

        {state.showOrdersOptions && (
          <VirtualizedTable
            type="compact"
            headerContent={{
              title: 'Retail Checkouts Report',
              subtitle: RETAIL_CHECKOUTS_TABLE_DESCRIPTION,
              action: {
                label: 'Download',
                onClick: () => queueExport('ORDERS'),
                icon: <FileDownloadOutlinedIcon />,
                disabled: isQueryFetching,
              },
            }}
            columns={ORDER_TABLE_COLUMNS}
            data={ordersTableData}
            height={480}
            isLoading={isOrdersLoading || ordersQuery.isRefetching}
            emptyText="No orders found for selected date range."
            onRowClick={onOrderClick}
            resetScrollKey={state.resetScrollKeys.orders}
            onEndReached={() => {
              if (ordersQuery.hasNextPage && !ordersQuery.isFetchingNextPage) {
                ordersQuery.fetchNextPage();
              }
            }}
          />
        )}
      </Box>
      {selectedAppointmentId && (
        <AppointmentSideBarModal
          isOpen
          appointmentId={selectedAppointmentId}
          onClose={() => setSelectedAppointmentId(null)}
        />
      )}
      {selectedPaymentId && (
        <PaymentsSideBarModal
          paymentId={selectedPaymentId}
          onClose={() => setSelectedPaymentId(null)}
        />
      )}
    </Stack>
  );
}
